import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FlaskConical, TrendingUp, AlertTriangle, Flame, Sparkles, RefreshCw, Power, ShieldCheck, Loader2 } from "lucide-react";

const SCENARIO_META = {
  excelente: {
    icon: TrendingUp, ring: "ring-emerald-500", text: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/40",
    desc: "KPIs saudáveis, SLAs em dia e planos concluídos — vitrine de operação madura.",
  },
  atencao: {
    icon: AlertTriangle, ring: "ring-amber-500", text: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/40",
    desc: "Volume médio com reincidências e SLAs em alerta — cenário realista de gestão.",
  },
  critica: {
    icon: Flame, ring: "ring-red-500", text: "text-red-400", bg: "bg-red-500/10 border-red-500/40",
    desc: "Alto volume, SLAs estourados e reincidências críticas — demonstra o poder de controle.",
  },
  transformacao: {
    icon: Sparkles, ring: "ring-violet-500", text: "text-violet-400", bg: "bg-violet-500/10 border-violet-500/40",
    desc: "Histórico ruim evoluindo para excelência — mostra o antes e depois da ASTRAL.",
  },
};

const COUNT_LABELS = {
  empresas: "Empresas", clientes: "Clientes", unidades: "Unidades", areas: "Áreas", setores: "Setores",
  responsaveis: "Responsáveis", supervisores: "Supervisores", tecnicos: "Técnicos", ocorrencias: "Ocorrências",
  planos: "Planos de Ação", fotos: "Fotos", narrativas_ia: "Narrativas IA", auditoria: "Auditoria",
  monitoramentos: "Monitoramentos", relatorios: "Relatórios", heatmaps: "Heatmaps",
};

export const DemoBaseCard = ({ onChanged }) => {
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState("critica");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const refresh = useCallback(() => api.get("/demo/status").then(({ data }) => {
    setStatus(data);
    if (data.scenario) setSelected(data.scenario);
  }).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);

  const run = async (action) => {
    setBusy(true);
    try {
      let data;
      if (action === "activate") ({ data } = await api.post("/demo/activate", { scenario: selected }));
      else if (action === "restore") ({ data } = await api.post("/demo/restore", { scenario: selected }));
      else ({ data } = await api.post("/demo/deactivate"));
      if (action === "deactivate") toast.success("Demonstração encerrada. Você voltou aos dados reais.");
      else toast.success(`Demonstração ${action === "restore" ? "regenerada" : "ativada"} — ${data.counts.total} registros fictícios no seu ambiente pessoal`);
      setConfirm(null);
      window.dispatchEvent(new Event("demo-changed"));
      await refresh();
      onChanged?.();
      // Troca de mundo (real <-> demonstração): recarrega para que todas as
      // telas e contextos leiam do ambiente certo.
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const active = status.active;
  const counts = status.counts || {};

  return (
    <Card className="p-6 border-violet-500/40 bg-violet-500/5 space-y-5" data-testid="demo-base-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading font-semibold flex items-center gap-2 text-violet-400">
          <FlaskConical size={18} /> Base Demonstrativa Enterprise
        </h3>
        <Badge variant="outline" data-testid="demo-status-badge"
          className={active ? "bg-violet-500/15 text-violet-400 border-violet-500/40" : "bg-muted text-muted-foreground"}>
          {active ? `ATIVA — ${status.scenarios?.[status.scenario] || status.scenario}` : "INATIVA"}
        </Badge>
      </div>

      <div className="rounded-md border border-border bg-card px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
        <ShieldCheck size={15} className="text-emerald-400 shrink-0 mt-0.5" />
        <span>
          Demonstração <b>pessoal</b>: os dados fictícios ficam num banco separado, só seu. Enquanto ela estiver ativa,
          <b>você</b> vê somente dados fictícios; os demais usuários e empresas continuam nos dados reais, sem nenhuma
          interferência. Configurações da plataforma ficam somente leitura e nada é enviado por e-mail ou push.
          Ao desativar, o banco demonstrativo é apagado e você volta aos dados reais.
        </span>
      </div>

      <div>
        <p className="text-sm font-medium mb-2.5">Cenário de apresentação</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(status.scenarios || {}).map(([key, label]) => {
            const meta = SCENARIO_META[key] || SCENARIO_META.critica;
            const Icon = meta.icon;
            const isSel = selected === key;
            return (
              <button key={key} type="button" onClick={() => setSelected(key)}
                className={`text-left rounded-lg border p-4 transition-all ${meta.bg} ${isSel ? `ring-2 ${meta.ring}` : "opacity-70 hover:opacity-100"}`}
                data-testid={`demo-scenario-${key}`}>
                <div className={`text-sm font-semibold flex items-center gap-2 ${meta.text}`}>
                  <Icon size={15} /> {label}
                  {active && status.scenario === key && <Badge variant="outline" className="text-[9px] ml-auto">atual</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{meta.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {!active ? (
          <Button onClick={() => setConfirm("activate")} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="demo-activate-btn">
            {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Power size={15} className="mr-2" />}
            {busy ? "Gerando base demonstrativa..." : "Ativar Base Demonstrativa"}
          </Button>
        ) : (
          <>
            {selected !== status.scenario && (
              <Button onClick={() => run("restore")} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="demo-switch-btn">
                {busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <FlaskConical size={15} className="mr-2" />}
                {busy ? "Aplicando cenário..." : "Aplicar cenário selecionado"}
              </Button>
            )}
            <Button variant="outline" onClick={() => run("restore")} disabled={busy} data-testid="demo-restore-btn">
              <RefreshCw size={14} className={`mr-2 ${busy ? "animate-spin" : ""}`} /> Regenerar dados
            </Button>
            <Button variant="destructive" onClick={() => setConfirm("deactivate")} disabled={busy} data-testid="demo-deactivate-btn">
              <Power size={14} className="mr-2" /> Desativar
            </Button>
          </>
        )}
      </div>

      {active && counts.total > 0 && (
        <div>
          <p className="text-sm font-medium mb-2.5">Registros demonstrativos gerados {status.seeded_at ? `· ${status.seeded_at.slice(0, 16).replace("T", " ")}` : ""}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="demo-counters">
            {Object.entries(COUNT_LABELS).map(([k, label]) => counts[k] > 0 && (
              <div key={k} className="rounded-md border border-border bg-card px-3 py-2.5" data-testid={`demo-count-${k}`}>
                <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
                <p className="font-heading text-lg font-bold">{counts[k]}</p>
              </div>
            ))}
            <div className="rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2.5" data-testid="demo-count-total">
              <p className="text-[10px] text-violet-400 leading-tight font-semibold">TOTAL</p>
              <p className="font-heading text-lg font-bold text-violet-400">{counts.total}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Os usuários fictícios existem só dentro da sua demonstração (aparecem nas telas, mas não fazem login).
          </p>
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="demo-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-violet-400">
              <FlaskConical size={17} /> {confirm === "activate" ? "Ativar Base Demonstrativa" : "Desativar Base Demonstrativa"}
            </DialogTitle>
          </DialogHeader>
          {confirm === "activate" ? (
            <p className="text-sm text-muted-foreground">
              Será gerado um ambiente demonstrativo completo no cenário <b className="text-foreground">{status.scenarios?.[selected]}</b> (clientes,
              unidades, ocorrências com fotos, planos de ação, SLA, reincidências e auditoria) num banco só seu. Os demais usuários
              não são afetados. A geração leva alguns segundos.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              O seu banco demonstrativo será apagado e você volta aos dados reais imediatamente, <b className="text-foreground">sem nenhuma
              alteração nos dados reais</b>.
            </p>
          )}
          <div className="flex gap-2">
            <Button onClick={() => run(confirm)} disabled={busy}
              className={confirm === "activate" ? "bg-violet-600 hover:bg-violet-700" : ""}
              variant={confirm === "activate" ? "default" : "destructive"} data-testid="demo-confirm-btn">
              {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
              {busy ? "Processando..." : confirm === "activate" ? "Confirmar e gerar" : "Confirmar desativação"}
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DemoBaseCard;
